import type {
  AzureStorageApi,
  AzureStorageIpcContract,
  AzureStorageIpcResponse,
} from '../../../../types/azureStorage';
import { cancelStorageRequest, getAzureStorageApi, newStorageRequestId, STORAGE_API_UNAVAILABLE } from '../services/azureStorageApi';
import { useStorageExplorerStore } from '../store/storageExplorerStore';

type QueueChannel = Extract<keyof AzureStorageIpcContract, `azure-storage:queue-${string}`>;
type QueueRequest<C extends QueueChannel> = AzureStorageIpcContract[C]['request'];
type WithoutRequestIdentity<T> = Omit<T, 'requestId'>;

export type QueueRequestHandle<T> = {
  requestId: string;
  promise: Promise<T>;
  cancel: () => void;
};

function unavailable<C extends QueueChannel>(): AzureStorageIpcResponse<C> {
  return { ok: false, error: STORAGE_API_UNAVAILABLE } as AzureStorageIpcResponse<C>;
}

function startQueueRequest<C extends QueueChannel>(
  tabId: string,
  _channel: C,
  request: WithoutRequestIdentity<QueueRequest<C>>,
  invoke: (api: AzureStorageApi, request: QueueRequest<C>) => Promise<AzureStorageIpcResponse<C>>,
): QueueRequestHandle<AzureStorageIpcResponse<C>> {
  const requestId = newStorageRequestId();
  const fullRequest = { ...request, requestId } as QueueRequest<C>;
  useStorageExplorerStore.getState().trackRequest(tabId, requestId);
  const api = getAzureStorageApi();
  const promise = (api ? invoke(api, fullRequest) : Promise.resolve(unavailable<C>())).finally(() => {
    useStorageExplorerStore.getState().untrackRequest(tabId, requestId);
  });
  return {
    requestId,
    promise,
    cancel: () => void cancelStorageRequest(requestId),
  };
}

type RequestFor<C extends QueueChannel> = WithoutRequestIdentity<QueueRequest<C>>;

export const startQueuePeek = (tabId: string, request: RequestFor<'azure-storage:queue-peek'>) =>
  startQueueRequest(tabId, 'azure-storage:queue-peek', request, (api, value) => api.queuePeek(value));
export const startQueueReceive = (tabId: string, request: RequestFor<'azure-storage:queue-receive'>) =>
  startQueueRequest(tabId, 'azure-storage:queue-receive', request, (api, value) => api.queueReceive(value));
export const startQueueSend = (tabId: string, request: RequestFor<'azure-storage:queue-send'>) =>
  startQueueRequest(tabId, 'azure-storage:queue-send', request, (api, value) => api.queueSend(value));
export const startQueueUpdate = (tabId: string, request: RequestFor<'azure-storage:queue-update'>) =>
  startQueueRequest(tabId, 'azure-storage:queue-update', request, (api, value) => api.queueUpdate(value));
export const startQueueMessageDelete = (tabId: string, request: RequestFor<'azure-storage:queue-message-delete'>) =>
  startQueueRequest(tabId, 'azure-storage:queue-message-delete', request, (api, value) => api.queueMessageDelete(value));
export const startQueueClear = (tabId: string, request: RequestFor<'azure-storage:queue-clear'>) =>
  startQueueRequest(tabId, 'azure-storage:queue-clear', request, (api, value) => api.queueClear(value));
export const startQueueCreate = (tabId: string, request: RequestFor<'azure-storage:queue-create'>) =>
  startQueueRequest(tabId, 'azure-storage:queue-create', request, (api, value) => api.queueCreate(value));
export const startQueueRemove = (tabId: string, request: RequestFor<'azure-storage:queue-remove'>) =>
  startQueueRequest(tabId, 'azure-storage:queue-remove', request, (api, value) => api.queueRemove(value));
